import { useState } from "react";
import { Button, Chip, Field, Skeleton } from "../../../design-v2";
import { countChars, formatCalendarDay, MAX_MEMORY_CHARS, todayKey } from "../domain/coachFormat";
import { MEMORY_KIND_OPTIONS } from "../domain/coachVocabulary";
import { ArrowRightIcon, PinIcon } from "./coachIcons";

/*
 * Settings > Personalization > Memory (blueprint §7.3 step 6, contract §5.5):
 * the only place where memory is managed. The overview reads like a summary,
 * grouped by kind, built from the stored memories only (no generated text).
 * One memory opens in its own editor: correct it, pin it, or delete it. The
 * source message is erased only when the athlete ticks that box.
 */

const REJECTION_COPY = {
  SENSITIVE: "Health, pain, body weight, mood and contact details aren't kept as memories.",
  INSTRUCTION_LIKE: "This reads like an instruction for the coach. Write it as a fact about you.",
  INVALID: "This can't be saved. Check the text and the date.",
};

export function memoryErrorCopy(error) {
  switch (error?.code) {
    case "COACH_MEMORY_REJECTED":
      return REJECTION_COPY[error.details?.reason] || REJECTION_COPY.INVALID;
    case "COACH_MEMORY_DUPLICATE":
      return "Your coach already remembers this.";
    case "COACH_MEMORY_LIMIT_REACHED":
      return "Memory is full. Delete a few items to add new ones.";
    case "VALIDATION_ERROR":
      return REJECTION_COPY.INVALID;
    case "COACH_MEMORY_NOT_FOUND":
      return "This memory no longer exists.";
    default:
      return "Couldn't save. Try again.";
  }
}

const GROUP_LABELS = Object.freeze({
  GOAL: "Goals",
  TRAINING_HABIT: "Habits",
  CONSTRAINT: "Constraints",
  EXERCISE_PREFERENCE: "Exercise preferences",
  TRIED_STRATEGY: "Tried before",
  COMMUNICATION: "How to talk to me",
});

export function MemoryOverview({ memories, status, canWrite, onRetry, onAdd, onOpen, onDeleteAll }) {
  if (status === "loading" && !memories) {
    return (
      <div className="lz-coach-settings__stack" aria-busy="true">
        <Skeleton height="3rem" />
        <Skeleton height="3rem" />
      </div>
    );
  }
  if (status === "error" && !memories) {
    return (
      <div className="lz-coach-sheet__state">
        <p>Couldn't load your memories.</p>
        <Button variant="ghost" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  const list = memories || [];
  const active = list.filter((memory) => memory.status === "ACTIVE");
  const expired = list.filter((memory) => memory.status !== "ACTIVE");
  const groups = MEMORY_KIND_OPTIONS.map((option) => ({
    kind: option.value,
    label: GROUP_LABELS[option.value],
    items: active.filter((memory) => memory.kind === option.value),
  })).filter((group) => group.items.length);

  const row = (memory) => (
    <li key={memory.id}>
      <button type="button" className="lz-coach-settings-row lz-coach-memory-row" onClick={() => onOpen(memory)}>
        <span className="lz-coach-settings-row__copy">
          <span className="lz-coach-memory-row__text">
            {memory.pinned ? <PinIcon /> : null}
            {memory.text}
          </span>
          {memory.validUntil ? (
            <span className="lz-coach-settings-row__description">
              {memory.status === "ACTIVE" ? `Until ${formatCalendarDay(memory.validUntil)}` : `Ended ${formatCalendarDay(memory.validUntil)}`}
            </span>
          ) : null}
        </span>
        <ArrowRightIcon />
      </button>
    </li>
  );

  return (
    <div className="lz-coach-settings__stack">
      <p className="lz-coach-sheet__lead">
        Your coach remembers what you state about yourself as you chat: habits, goals, constraints. It never saves a guess.
        It uses these to personalize its answers.
      </p>
      {canWrite ? (
        <Button variant="secondary" className="lz-coach-sheet__wide" onClick={onAdd}>
          Add to memory
        </Button>
      ) : null}
      {!list.length ? <p className="lz-coach-sheet__empty">Nothing yet. Tell your coach about a habit or a goal and it shows up here.</p> : null}
      {groups.map((group) => (
        <section key={group.kind} className="lz-coach-settings-group" aria-label={group.label}>
          <h3 className="lz-coach-settings-group__title">{group.label}</h3>
          <ul className="lz-coach-settings-group__rows">{group.items.map(row)}</ul>
        </section>
      ))}
      {expired.length ? (
        <section className="lz-coach-settings-group" aria-label="No longer used">
          <h3 className="lz-coach-settings-group__title">No longer used</h3>
          <ul className="lz-coach-settings-group__rows lz-coach-settings-group__rows--muted">{expired.map(row)}</ul>
        </section>
      ) : null}
      {list.length ? (
        <button type="button" className="lz-coach-text-button lz-coach-text-button--danger" onClick={onDeleteAll}>
          Delete all memories
        </button>
      ) : null}
    </div>
  );
}

export function MemoryForm({ initial, onSave, onCancel }) {
  const [kind, setKind] = useState(initial?.kind || "TRAINING_HABIT");
  const [text, setText] = useState(initial?.text || "");
  const [validUntil, setValidUntil] = useState(initial?.validUntil || "");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const length = countChars(text);
  const tooLong = length > MAX_MEMORY_CHARS;

  async function submit(event) {
    event.preventDefault();
    if (!length || tooLong) return;
    setSaving(true);
    setError(null);
    const message = await onSave({ kind, text: text.trim(), validUntil: validUntil || null });
    setSaving(false);
    if (message) setError(message);
  }

  return (
    <form className="lz-coach-memory-form" onSubmit={submit}>
      <Field
        label={initial ? "Memory" : "What should your coach remember?"}
        hint={`${length} / ${MAX_MEMORY_CHARS}`}
        error={error || (tooLong ? `Keep it under ${MAX_MEMORY_CHARS} characters.` : null)}
        required
        optionalLabel={null}
      >
        <textarea
          className="lz-coach-textarea"
          rows={2}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="I train better in the morning."
        />
      </Field>
      <div className="lz-coach-chip-group" role="group" aria-label="Kind of memory">
        {MEMORY_KIND_OPTIONS.map((option) => (
          <Chip key={option.value} selected={kind === option.value} onClick={() => setKind(option.value)}>
            {option.label}
          </Chip>
        ))}
      </div>
      {kind === "CONSTRAINT" || validUntil ? (
        <Field label="Until" hint="For a temporary constraint. It stops being used after this day.">
          <input type="date" className="lz-coach-input" value={validUntil} min={todayKey()} onChange={(event) => setValidUntil(event.target.value)} />
        </Field>
      ) : null}
      <div className="lz-coach-form-actions">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" isLoading={saving} loadingLabel="Saving" disabled={!length || tooLong}>
          Save
        </Button>
      </div>
    </form>
  );
}

// One memory: correct it, pin it, delete it. Erasing the source message is an
// explicit choice, unticked by default.
export function MemoryEditor({ memory, canWrite, onSave, onTogglePin, onDelete, onDone }) {
  const [redactSource, setRedactSource] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [error, setError] = useState(null);

  return (
    <div className="lz-coach-settings__stack">
      {canWrite ? (
        <>
          <MemoryForm initial={memory} onSave={onSave} onCancel={onDone} />
          <div className="lz-coach-switch-row">
            <div className="lz-coach-switch-row__copy">
              <span className="lz-coach-switch-row__label" id="lz-coach-memory-pin">Pin</span>
              <span className="lz-coach-switch-row__description">Your coach always keeps a pinned memory in mind.</span>
            </div>
            <button
              type="button"
              role="switch"
              aria-labelledby="lz-coach-memory-pin"
              aria-checked={Boolean(memory.pinned)}
              className="lz-coach-switch"
              disabled={pinning}
              onClick={async () => {
                setPinning(true);
                setError(await onTogglePin());
                setPinning(false);
              }}
            >
              <span aria-hidden="true" className="lz-coach-switch__thumb" />
            </button>
          </div>
        </>
      ) : (
        <p className="lz-coach-memory-row__text">{memory.text}</p>
      )}

      <section className="lz-coach-confirm lz-coach-confirm--danger" aria-label="Delete this memory">
        <p className="lz-coach-confirm__title">Delete this memory</p>
        <p className="lz-coach-confirm__body">Your coach stops using it. Earlier replies in the conversation may still mention it.</p>
        {memory.sourceEventId ? (
          <label className="lz-coach-checkbox">
            <input type="checkbox" checked={redactSource} onChange={(event) => setRedactSource(event.target.checked)} />
            <span>Also erase the message where you said it</span>
          </label>
        ) : null}
        {error ? <p className="lz-coach-inline-error" role="alert">{error}</p> : null}
        <div className="lz-coach-form-actions">
          <Button
            variant="danger"
            size="sm"
            isLoading={deleting}
            loadingLabel="Deleting"
            onClick={async () => {
              setDeleting(true);
              const message = await onDelete({ redactSource: Boolean(memory.sourceEventId) && redactSource });
              if (message) {
                setDeleting(false);
                setError(message);
              }
            }}
          >
            Delete memory
          </Button>
        </div>
      </section>
    </div>
  );
}
